// Decides how the design tool opens a design (PAD-32396). Dependency free so scripts/test-figma-edit.mjs can run it.

export interface BuilderParamsDesign {
  readonly isUploaded?: boolean | null;
  readonly s3Link?: string | null;
  readonly hasHtmlContent?: boolean | null;
  readonly designSource?: string | null;
}

export type BuilderImportParams =
  | { readonly kind: "pdfImport"; readonly fileLink: string; readonly hasHtmlContent: false }
  | { readonly kind: "needsFigmaChoice" }
  | { readonly kind: "none" };

export const FIGMA_DESIGN_SOURCE = "figma";

export const getBuilderImportParams = (design: BuilderParamsDesign): BuilderImportParams => {
  // A Figma design has no editable canvas, so the user must pick copy or flatten first.
  if (design.designSource === FIGMA_DESIGN_SOURCE) return { kind: "needsFigmaChoice" };
  if (design.isUploaded && design.s3Link && !design.hasHtmlContent) {
    return { kind: "pdfImport", fileLink: design.s3Link, hasHtmlContent: false };
  }
  return { kind: "none" };
};

export interface PreparedEditSearchInput {
  readonly designId: string;
  readonly fileLink: string;
  readonly autoAddAddressBox?: boolean;
  // Existing query string to keep (variant, action and so on); designId only replaces it when present.
  readonly baseSearch?: string;
  readonly setDesignIdParam?: boolean;
}

// Builds the query that makes PDFImport place the prepared rendition on the canvas.
export const buildPreparedEditSearch = ({
  designId,
  fileLink,
  autoAddAddressBox = false,
  baseSearch = "",
  setDesignIdParam = true,
}: PreparedEditSearchInput): string => {
  const params = new URLSearchParams(baseSearch);
  if (setDesignIdParam) params.set("designId", designId);
  params.set("fileLink", fileLink);
  params.set("hasHtmlContent", "false");
  if (autoAddAddressBox) params.set("autoAddAddressBox", "true");
  return `?${params.toString()}`;
};

// True when the URL already carries a PDF to import, so no Figma choice is pending.
export const isPreparedPdfImport = (search: string): boolean => {
  const params = new URLSearchParams(search);
  return Boolean(params.get("fileLink")) && params.get("hasHtmlContent") === "false";
};

export type AddressBoxEntry =
  | { readonly kind: "needsFigmaChoice" }
  | { readonly kind: "missingPdf" }
  | { readonly kind: "open"; readonly fileLink: string | null };

// How "Add address box" opens a design (PAD-32395); a Figma design is checked first because activateDesign sets isUploaded on it.
export const getAddressBoxEntry = (design: BuilderParamsDesign): AddressBoxEntry => {
  const importParams = getBuilderImportParams(design);
  if (importParams.kind === "needsFigmaChoice") return importParams;
  if (design.isUploaded && !design.s3Link) return { kind: "missingPdf" };
  return { kind: "open", fileLink: importParams.kind === "pdfImport" ? importParams.fileLink : null };
};

// Same param order the address box flow always used, so uploaded and Flyer Builder URLs stay identical.
export const buildAddressBoxSearch = (
  designId: string,
  fileLink: string | null,
  orientation: string | null = null
): string => {
  const params = new URLSearchParams();
  params.set("designId", designId);
  params.set("autoAddAddressBox", "true");
  if (fileLink) {
    params.set("fileLink", fileLink);
    params.set("hasHtmlContent", "false");
    // The Liveblocks reconcile honours this over the design's saved orientation (#2581).
    if (orientation) params.set("orientation", orientation);
  }
  return `?${params.toString()}`;
};

// The platform embeds only gate action=edit; the campaign route gates any open of an existing design.
export const shouldCheckDesignOpen = (search: string, requireEditAction: boolean): boolean => {
  const params = new URLSearchParams(search);
  const action = params.get("action");
  if (action === "create" || params.get("newDesign") === "true") return false;
  if (requireEditAction && action !== "edit") return false;
  return !isPreparedPdfImport(search);
};

export interface FigmaGateCheckInput {
  readonly designId?: string | null;
  readonly search: string;
  readonly requireEditAction: boolean;
  // Ids the builder has already rendered for in this mount; updated in place.
  readonly passedIds: Set<string>;
}

// One entry check per design and mount: a skipped open is recorded too, so later URL rewrites never unmount the builder.
export const figmaGateNeedsCheck = ({
  designId,
  search,
  requireEditAction,
  passedIds,
}: FigmaGateCheckInput): boolean => {
  if (!designId) return false;
  const id = String(designId);
  if (passedIds.has(id)) return false;
  if (shouldCheckDesignOpen(search, requireEditAction)) return true;
  passedIds.add(id);
  return false;
};

export interface DesignEditInfo {
  readonly designSource?: string | null;
  readonly hasFigmaPlaceholders?: boolean | null;
  readonly usedByCampaign?: boolean | null;
}

// Address box flows run after this campaign selected the design, so usage comes from a fresh lookup; a failed one is unknown.
export const withFreshEditInfo = <T extends DesignEditInfo>(design: T, info: DesignEditInfo | null): T => ({
  ...design,
  designSource: info?.designSource ?? design.designSource,
  hasFigmaPlaceholders: info?.hasFigmaPlaceholders ?? design.hasFigmaPlaceholders ?? null,
  usedByCampaign: info ? info.usedByCampaign ?? null : null,
});
